import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import { SiteService } from 'src/app/services/site.service';
import { UserService } from 'src/app/services/user.service';
import { Notification, NotificationLevel } from 'src/app/model/notification';
import { AppState } from 'src/app/store/app.reducer';
import * as SiteListActions from './site-list.actions';
import * as NotificationsActions from '../notifications/notifications.actions';


@Injectable()
export class SiteListEffects {
    getSites$ = createEffect(() => this.actions$.pipe(
        ofType(SiteListActions.getSitesData),
        withLatestFrom(this.store.select('user', 'currentUser')),
        switchMap(([action, currentUser]) => this.siteService.getSites().pipe(
            map((resp) => {
                if (resp.Success) {
                    const newSites = resp.Data.map(site => ({ ...site, CurrentlyOwned: site.UserId === currentUser.Id }));
                    return SiteListActions.getSitesDataSuccess({ sites: newSites });
                } else {
                    return SiteListActions.getSitesDataFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(SiteListActions.getSitesDataFailed({ errorMessage: error.message }));
            })
        ))
    ));

    deleteSite$ = createEffect(() => this.actions$.pipe(
        ofType(SiteListActions.deleteSite),
        switchMap((action) => this.siteService.deleteSite(action.siteId).pipe(
            map((resp) => {
                if (resp.Success) {
                    return SiteListActions.deleteSiteSuccess({
                        siteId: action.siteId,
                        message: this.translate.instant('SITE_DELETE_SUCCESS')
                    });
                } else {
                    return SiteListActions.deleteSiteFailed({ errorMessage: resp.ErrorMessage });
                }
            }),
            catchError(error => {
                console.error(error);
                return of(SiteListActions.deleteSiteFailed({ errorMessage: error.message }));
            })
        ))
    ));

    /*loadSitesOwner$ = createEffect(() => this.actions$.pipe(
        ofType(SiteListActions.getSitesDataSuccess),
        switchMap((action) => action.sites.map(site => SiteListActions.getSiteOwner({ site })))
    ));*/

    ticketOpened$ = createEffect(() => this.actions$.pipe(
        ofType(SiteListActions.getSiteOwner),
        mergeMap((action) => this.userService.getUserById(action.site.UserId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return SiteListActions.getSiteOwnerSuccess({ siteId: action.site.Id, user: resp.Data });
                    } else {
                        return SiteListActions.getSiteOwnerFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SiteListActions.getSiteOwnerFailed({ errorMessage: error.message })))
            ))
    ));

    getSiteOwner$ = createEffect(() => this.actions$.pipe(
        ofType(SiteListActions.getSiteOwner),
        mergeMap((action) => this.userService.getUserById(action.site.UserId)
            .pipe(
                map(resp => {
                    if (resp.Success) {
                        return SiteListActions.getSiteOwnerSuccess({ siteId: action.site.Id, user: resp.Data });
                    } else {
                        return SiteListActions.getSiteOwnerFailed({ errorMessage: resp.ErrorMessage });
                    }
                }),
                catchError(error => of(SiteListActions.getSiteOwnerFailed({ errorMessage: error.message })))
            ))
    ));

    notifySuccess$ = createEffect(() => this.actions$.pipe(
        ofType(
            SiteListActions.deleteSiteSuccess,
        ),
        tap((action) => {
            if (action.message === '') {
                return;
            }
            const notification = new Notification("SITE_LIST", action.message, NotificationLevel.Success, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });


    notifyError$ = createEffect(() => this.actions$.pipe(
        ofType(
            SiteListActions.deleteSiteFailed,
        ),
        tap((action) => {
            if (action.errorMessage === '') {
                return;
            }
            const notification = new Notification("SITE", action.errorMessage, NotificationLevel.Error, '');
            this.store.dispatch(NotificationsActions.addNotification({ notification: notification, toastOnly: true }));
        })
    ), { dispatch: false });

    constructor(
        private actions$: Actions,
        private store: Store<AppState>,
        private siteService: SiteService,
        private userService: UserService,
        private translate: TranslateService,
    ) { }
}

